import { useCallback } from "react";
import { useTranslation } from "react-i18next";
import { Brain, Footprints, Hand, HeartPulse, ScanFace, Stethoscope } from "lucide-react";

/** t("key", "English fallback", { params }) under the `anatomyRecall` namespace. */
export function useRecallT() {
  const { t, i18n } = useTranslation();
  const tr = useCallback(
    (key, fallback, params) => t(`anatomyRecall.${key}`, { defaultValue: fallback, ...(params || {}) }),
    [t],
  );
  return { t: tr, lang: i18n.language || "en" };
}

export const REGION_ICONS = {
  "head-neck": ScanFace,
  "thorax-spine": HeartPulse,
  "upper-limb": Hand,
  "lower-limb": Footprints,
  "abdomen-pelvis": Stethoscope,
  nervous: Brain,
};

export function gradientVars(gradient) {
  return { "--g0": gradient[0], "--g1": gradient[1], "--g2": gradient[2] || gradient[1] };
}

export function ProgressRing({ size, stroke = 6, percent, colors = ["#FB7185", "#E11D48"], track, children }) {
  const radius = (size - stroke) / 2;
  const circumference = 2 * Math.PI * radius;
  const value = Math.max(0, Math.min(100, percent || 0)) / 100;
  const id = `arg-${colors[0].slice(1)}-${colors[1].slice(1)}`;
  return (
    <div className="ar-ring" style={{ width: size, height: size }}>
      <svg width={size} height={size} aria-hidden>
        <defs>
          <linearGradient id={id} x1="0" y1="0" x2="1" y2="1">
            <stop offset="0" stopColor={colors[0]} />
            <stop offset="1" stopColor={colors[1]} />
          </linearGradient>
        </defs>
        <circle cx={size / 2} cy={size / 2} r={radius} fill="none" stroke={track || "var(--ar-track)"} strokeWidth={stroke} />
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="none"
          stroke={`url(#${id})`}
          strokeWidth={stroke}
          strokeLinecap="round"
          strokeDasharray={`${circumference} ${circumference}`}
          strokeDashoffset={circumference * (1 - value)}
        />
      </svg>
      <div className="ar-ring-center">{children}</div>
    </div>
  );
}

export const STATUS_META = {
  notStarted: ["statusNotStarted", "Not started", "#94A3B8"],
  learning: ["statusLearning", "Learning", "#F59E0B"],
  practicing: ["statusPracticing", "Practicing", "#38BDF8"],
  mastered: ["statusMastered", "Mastered", "#22C55E"],
};

export function StatusChip({ status, comingSoon, t }) {
  const [key, fallback, color] = comingSoon
    ? ["comingSoon", "Coming soon", "#A78BFA"]
    : STATUS_META[status] || STATUS_META.notStarted;
  return (
    <span className="ar-chip" style={{ "--c": color }}>
      {t(key, fallback)}
    </span>
  );
}
